import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { authApi, ApiError } from '../services/api';
import { X, Mail, AlertCircle, KeyRound, CheckCircle, ArrowLeft } from 'lucide-react';

interface ForgotPasswordModalProps {
  onClose: () => void;
  onSwitchToLogin: () => void;
}

interface FormErrors {
  email?: string;
  general?: string;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ onClose, onSwitchToLogin }) => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(value);

    if (errors.email) {
      setErrors(prev => ({ ...prev, email: undefined }));
    }
  };

  const handleBlur = () => {
    setTouched(prev => ({ ...prev, email: true }));
    validateField();
  };

  const validateField = (): boolean => {
    let isValid = true;
    const newErrors = { ...errors };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!emailRegex.test(email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    } else {
      delete newErrors.email;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setTouched({ email: true });
    if (!validateField()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await authApi.forgotPassword({ email });
      setIsSubmitted(true);
    } catch (error) {
      const apiError = error as { response?: { data?: ApiError } };
      if (apiError.response?.data) {
        const errorData = apiError.response.data;
        setErrors({ general: errorData.message || 'Failed to send reset link. Please try again.' });
      } else {
        setErrors({ general: 'Failed to send reset link. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative bg-gradient-to-br from-fintech-primary to-blue-800 px-8 pt-8 pb-10">
          <button
            className="absolute top-4 right-4 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-4">
            <KeyRound size={22} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-1">Reset Password</h2>
          <p className="text-blue-200 text-sm">
            {isSubmitted
              ? 'Check your inbox for the reset link'
              : "Enter your email and we'll send you a reset link"
            }
          </p>
        </div>

        {/* Content */}
        <div className="px-8 py-7">
          {isSubmitted ? (
            /* Success State */
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={32} className="text-fintech-accent" />
              </div>
              <h3 className="text-lg font-bold text-fintech-text mb-2">Reset Link Sent!</h3>
              <p className="text-fintech-textMuted text-sm mb-2">
                We've sent a password reset link to:
              </p>
              <p className="font-semibold text-fintech-primary text-sm mb-5 bg-blue-50 px-4 py-2 rounded-lg inline-block">
                {email}
              </p>
              <p className="text-xs text-fintech-textMuted mb-7 leading-relaxed">
                Please check your inbox and follow the instructions to reset your password. The link expires in 1 hour.
              </p>
              <button
                type="button"
                className="btn-primary w-full justify-center"
                onClick={onClose}
              >
                Back to Login
              </button>
            </div>
          ) : (
            /* Form State */
            <>
              {errors.general && (
                <div className="alert alert-error mb-5">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  <span>{errors.general}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                <div className="form-group mb-0">
                  <label className="form-label" htmlFor="forgot-email">Email Address</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-fintech-textMuted pointer-events-none" />
                    <input
                      type="email"
                      id="forgot-email"
                      value={email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`form-input !pl-10 ${touched.email && errors.email ? 'error' : ''}`}
                      placeholder="you@company.com"
                      required
                    />
                  </div>
                  {touched.email && errors.email && (
                    <p className="form-error">
                      <AlertCircle size={12} />
                      {errors.email}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full justify-center py-3"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner w-4 h-4" />
                      Sending Reset Link...
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Send Reset Link
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="divider mt-6">
                <span>Remember your password?</span>
              </div>
              <button
                type="button"
                className="btn-secondary w-full justify-center"
                onClick={onSwitchToLogin}
              >
                <ArrowLeft size={16} />
                Back to Sign In
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default ForgotPasswordModal;
